import React, { useState } from 'react';
import {
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Alert,
} from '@mui/material';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';

const TIME_SLOTS = [
  'So schnell wie möglich',
  'Heute Vormittag (9–12 Uhr)',
  'Heute Nachmittag (12–17 Uhr)',
  'Morgen Vormittag (9–12 Uhr)',
  'Morgen Nachmittag (12–17 Uhr)',
];

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const CallbackWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', preferred_time: '', message: '' });

  const handleChange = (e: any) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Bitte geben Sie Ihren Namen ein.'); return; }
    if (!form.phone.trim()) { setError('Bitte geben Sie Ihre Telefonnummer ein.'); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/callback`, form);
      setSubmitted(true);
    } catch {
      setError('Es gab einen Fehler. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: '', phone: '', preferred_time: '', message: '' });
      setError('');
    }, 300);
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        <Collapse in={!open} timeout={200}>
          <Box
            sx={{
              bgcolor: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              boxShadow: 3,
              mb: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="body2" fontWeight={600} color="primary">
              Kostenloser Rückruf
            </Typography>
          </Box>
        </Collapse>
        <Fab
          color="primary"
          size="large"
          onClick={() => setOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 20px rgba(102,126,234,0.5)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd6 0%, #6a3d92 100%)',
            },
          }}
          aria-label="Rückruf anfordern"
        >
          <PhoneCallbackIcon />
        </Fab>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Typography variant="h6" fontWeight={700}>
            Kostenloser Rückruf
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Wir rufen Sie innerhalb von 24h zurück.
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {submitted ? (
            <Box textAlign="center" py={3}>
              <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Danke!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Wir melden uns so schnell wie möglich bei Ihnen unter der angegebenen Nummer.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Ihr Name *"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Max Mustermann"
              />
              <TextField
                label="Telefonnummer *"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="+49 30 12345678"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Wann können wir anrufen?</InputLabel>
                <Select
                  name="preferred_time"
                  value={form.preferred_time}
                  onChange={handleChange}
                  label="Wann können wir anrufen?"
                >
                  {TIME_SLOTS.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Kurze Nachricht (optional)"
                name="message"
                value={form.message}
                onChange={handleChange}
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Worum geht es? z.B. Inbound für 500 Anrufe/Monat"
              />
            </Box>
          )}
        </DialogContent>

        {!submitted && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleClose} disabled={isLoading}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 3,
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Rückruf anfordern'}
            </Button>
          </DialogActions>
        )}

        {submitted && (
          <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
            <Button variant="contained" onClick={handleClose}>Schließen</Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default CallbackWidget;
